import { Button } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import PropTypes from 'prop-types';

/**
 * Base button component that other button components extend from
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 * @param {Object} props.rest - Additional button props
 */
const BaseButton = ({ className = '', children, ...rest }) => (
  <Button className={className} {...rest}>
    {children}
  </Button>
);

BaseButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

/**
 * Primary button component
 */
const PrimaryButton = ({ children, ...props }) => (
  <BaseButton className="primary-button" type="primary" {...props}>
    {children}
  </BaseButton>
);

/**
 * Secondary button component
 */
const SecondaryButton = ({ children, ...props }) => (
  <BaseButton className="secondary-button" type="default" {...props}>
    {children}
  </BaseButton>
);


/**
 * Cancel button component
 */
const CancelButton = ({ children, ...props }) => (
  <BaseButton className="cancel-button" type="danger" {...props}>
    {children}
  </BaseButton>
);

/**
 * Finish button component
 */
const FinishButton = ({ children, ...props }) => (
  <BaseButton className="primary-button" type="default" {...props}>
    {children}
  </BaseButton>
);

/**
 * Add button component with optional icon
 */
const AddButton = ({ withIcon = true, children, ...props }) => (
  <BaseButton
    className="add-button d-flex align-items-center justify-content-center"
    type="default"
    icon={withIcon ? <PlusOutlined /> : null}
    {...props}
  >
    {children}
  </BaseButton>
);

/**
 * Remove button component with optional icon
 */
const RemoveButton = ({ withIcon = true, children, ...props }) => (
  <BaseButton
    className="cancel-button d-flex justify-content-center align-items-center"
    type="danger"
    icon={withIcon ? <DeleteOutlined className={children === null ? "mb-1" : ""} /> : null}
    {...props}
  >
    {children}
  </BaseButton>
);

// Add PropTypes for all components
const buttonPropTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

PrimaryButton.propTypes = buttonPropTypes;
SecondaryButton.propTypes = buttonPropTypes;
CancelButton.propTypes = buttonPropTypes;
FinishButton.propTypes = buttonPropTypes;

AddButton.propTypes = {
  ...buttonPropTypes,
  withIcon: PropTypes.bool,
};

RemoveButton.propTypes = {
  ...buttonPropTypes,
  withIcon: PropTypes.bool,
};

export {
  PrimaryButton,
  SecondaryButton,
  CancelButton,
  FinishButton,
  AddButton,
  RemoveButton,
};
